import EditIcon from '@mui/icons-material/Edit';
import { Box, Button, Grid, Paper, Menu, MenuItem, Tab, Tabs, Typography, Divider } from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { deleteUser, restoreUser } from '../../../../../api/user';
import { loginAsUserReq } from '../../../../../redux/slices/user';
import { updateEnableStatus, userRoleData } from '../../../../../redux/slices/users';
import { useDispatch, useSelector } from '../../../../../redux/store';
import { ConfirmDialog } from '../../../../../reusable-components/confirm-dialog';
import Iconify from '../../../../../reusable-components/iconify';
import { ScopeGuard, useScopeCheck } from '../../../../../reusable-components/scopes';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import { TabPanel } from '../../../../../reusable-components/tab-panel';
import GeneralInfoTab from './GeneralInfoTab';
import RolesTab from './RolesTab';
import SecurityTab from './SecurityTab';
import { AwaitButton } from '../../../../../reusable-components/await-button';
import SSOTab from './SSOTab';
import UserOrdersTab from './UserOrdersTab';
import UserRentalsTab from './UserRentalsTab';
import DropdownAction from '../../../../../reusable-components/dropdown-action/DropdownAction';

export default function UserView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { id = '' } = useParams();
  const [value, setValue] = useState(0);
  const [edit, setEdit] = useState(false);
  const [userEnabled, setUserEnabled] = useState(false);
  const { user, users } = useSelector((state) => state.users.data);
  const { user: account } = useSelector((state) => state.user);
  const { enqueueSnackbar } = useSnackbar();
  const hasEditAccess = useScopeCheck(['User-Edit'], true);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setUserEnabled(new Date(user.lockoutEnd) < new Date());
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const { firstName, lastName, email, facility, position } = user;

  useEffect(() => {
    const tab = new URLSearchParams(search).get('tab');
    if (tab !== null) {
      setValue(Number(tab));
    } else {
      setValue(0);
    }
  }, [search]);

  useEffect(() => {
    dispatch(userRoleData(id, users.length));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  const handleTabChange = (event, newValue) => {
    navigate(`?tab=${newValue}`);
    setValue(newValue);
    setEdit(false);
  };
  const handleEdit = () => {
    setEdit(true);
  };
  const loginAsUser = () => {
    dispatch(loginAsUserReq(email));
  };

  const handleDelete = async () => {
    const response = await deleteUser(id);
    if (response) window.location.href = '/dashboard/admin/users';
  };

  const handleRestoreUser = async () => {
    const response = await restoreUser(id);
    if (response) window.location.href = '/dashboard/admin/users';
  };

  const handleDisable = async () => {
    const enable = new Date(user.lockoutEnd) >= new Date();
    const response = await dispatch(updateEnableStatus(id, enable));

    if (response.data) enqueueSnackbar(`User ${enable ? 'en' : 'dis'}abled`, { variant: 'success' });
    else enqueueSnackbar(`User could not be ${enable ? 'en' : 'dis'}abled`, { variant: 'error' });

    setAnchorEl(null);
  };

  return (
    <Paper sx={{ mt: 3, px: 10, pt: 2, pb: 6, width: '100%', height: '100%' }}>
      <Grid container direction="column" justifyContent="center" alignItems="flex-start">
        <Grid item>
          <Typography variant="subtitle1" color="secondary" display="inline">
            {firstName} {lastName}
          </Typography>
          <Typography display="inline"> | {facility}</Typography>
        </Grid>
        <Grid item>
          <Typography display="inline">{position}</Typography>
          <Typography display="inline"> | {email}</Typography>
        </Grid>

        <Grid sx={{ mb: 1 }} container direction="row" justifyContent="space-between" alignItems="center" spacing={7}>
          <Grid item>
            <Box>
              <Tabs value={value} onChange={handleTabChange}>
                <Tab label="General information" />
                <Tab label="Security" />
                <Tab label="Roles" />
                <Tab label="SSO" />
                <Tab label="Recent Orders" />
                <Tab label="Recent Rentals" />
              </Tabs>
            </Box>
          </Grid>
          <Grid item>
            <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
              {(value < 2 || value === 3) && (
                <>
                  <ScopeGuard scopes={['User-Edit']} allowAdmin>
                    <AwaitButton
                      color="secondary"
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      sx={{ mr: 2 }}
                      onClick={handleEdit}
                    >
                      Edit
                    </AwaitButton>
                    <ScopeGuard allowAdmin>
                      <AwaitButton
                        color="secondary"
                        variant="outlined"
                        size="small"
                        startIcon={<Iconify icon="ic:outline-log-in" />}
                        sx={{ mr: 2 }}
                        onClick={loginAsUser}
                        disabled={account.id === id || user.deleteFlag}
                      >
                        Log in as
                      </AwaitButton>
                    </ScopeGuard>

                    <Button
                      sx={{
                        ml: 0,
                        minWidth: 0,
                        px: 1,
                      }}
                      variant="outlined"
                      size="small"
                      color="secondary"
                      onClick={handleClick}
                    >
                      <Iconify icon="mdi:more-vert" />
                    </Button>

                    <Menu
                      id="basic-menu"
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      slotProps={{
                        list: { 'aria-labelledby': 'basic-button' },
                        paper: {
                          sx: {
                            width: 150,
                            p: 1,
                            borderRadius: '5px',
                            boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
                            '& .MuiMenu-list': {
                              p: 0,
                              display: 'flex',
                              flexDirection: 'column',
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem
                        disableRipple
                        disableGutters
                        sx={{ p: 0, minHeight: 'unset', '&:hover': { bgcolor: 'transparent' } }}
                      >
                        <DropdownAction
                          buttonComponent={AwaitButton}
                          badgeIcon={userEnabled ? 'mdi:account-off-outline' : 'mdi:account-check-outline'}
                          onClick={handleDisable}
                          disabled={account.id === id}
                        >
                          {userEnabled ? 'Disable' : 'Enable'}
                        </DropdownAction>
                      </MenuItem>

                      <Divider sx={{ my: 0.5, mx: 2 }} />

                      <MenuItem disableRipple disableGutters sx={{ p: 0, minHeight: 'unset' }}>
                        <DropdownAction
                          buttonComponent={ConfirmDialog}
                          badgeIcon={user?.deleteFlag ? 'mdi:refresh' : 'mdi:delete-outline'}
                          iconColor={user?.deleteFlag ? 'success.main' : 'error.main'}
                          iconBgColor={user?.deleteFlag ? '#f3fded' : '#FDEDED'}
                          textColor={user?.deleteFlag ? 'text.main' : 'error.main'}
                          button="text"
                          title={user?.deleteFlag ? 'Restore' : 'Delete'}
                          color={user?.deleteFlag ? 'primary' : 'error'}
                          content={`Are you sure you want to ${user?.deleteFlag ? 'restore' : 'delete'} this user?`}
                          actionButton={user?.deleteFlag ? 'Restore' : 'Delete'}
                          action={user?.deleteFlag ? handleRestoreUser : handleDelete}
                          icon={user?.deleteFlag ? 'refresh' : 'delete-outline'}
                          tooltip={user?.deleteFlag ? 'Restore' : 'Delete'}
                        >
                          {user?.deleteFlag ? 'Restore' : 'Delete'}
                        </DropdownAction>
                      </MenuItem>
                    </Menu>
                  </ScopeGuard>
                </>
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>

      <TabPanel value={value} index={0}>
        {user && <GeneralInfoTab edit={edit} setEdit={setEdit} user={user} />}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {user && <SecurityTab edit={edit} setEdit={setEdit} user={user} />}
      </TabPanel>
      <TabPanel value={value} index={2}>
        {user && <RolesTab edit={edit} setEdit={setEdit} user={user} />}
      </TabPanel>
      <TabPanel value={value} index={3}>
        {user && <SSOTab edit={edit} setEdit={setEdit} user={user} />}
      </TabPanel>
      <TabPanel value={value} index={4}>
        {user && <UserOrdersTab user={user} />}
      </TabPanel>
      <TabPanel value={value} index={5}>
        {user && <UserRentalsTab user={user} />}
      </TabPanel>
    </Paper>
  );
}
